import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

/** Compact autocomplete for forms (vs the big hero variant in BookAutocomplete). */
export default function BookAutocompleteInline({
    value,
    onChange,
    onPick,
    placeholder,
    inputClassName,
    testId = "inline-autocomplete",
}) {
    const { t } = useTranslation();
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [loading, setLoading] = useState(false);
    const wrapperRef = useRef(null);
    const abortRef = useRef(null);
    const skipFetchRef = useRef(false);

    const fetchSuggestions = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setSuggestions([]);
            setLoading(false);
            return;
        }
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        try {
            const { data } = await api.get("/books/search", {
                params: { q, limit: 6 },
                signal: controller.signal,
            });
            setSuggestions(Array.isArray(data) ? data : []);
            setHighlight(-1);
        } catch (err) {
            if (err.name !== "CanceledError" && err.name !== "AbortError") {
                setSuggestions([]);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Skip the fetch that would otherwise run right after the user picks
        // a suggestion (we don't want to re-query for the full picked title).
        if (skipFetchRef.current) {
            skipFetchRef.current = false;
            return;
        }
        // 200ms debounce — feels instant on type-ahead while batching keystrokes.
        const t1 = setTimeout(() => fetchSuggestions(value), 200);
        return () => clearTimeout(t1);
    }, [value, fetchSuggestions]);

    useEffect(() => {
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handlePick = (item) => {
        skipFetchRef.current = true;
        if (abortRef.current) abortRef.current.abort();
        onChange(item.title);
        setOpen(false);
        setSuggestions([]);
        setLoading(false);
        if (onPick) onPick(item);
    };

    const handleKeyDown = (e) => {
        if (!open || !suggestions.length) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, -1));
        } else if (e.key === "Enter" && highlight >= 0) {
            e.preventDefault();
            handlePick(suggestions[highlight]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                data-testid={testId}
                type="text"
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || t("home.search_placeholder")}
                className={inputClassName}
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={open && suggestions.length > 0}
            />
            {loading && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 h-1.5 w-1.5 bg-[var(--accent)] rounded-full animate-slow-pulse" />
            )}

            {open && suggestions.length > 0 && (
                <ul
                    data-testid={`${testId}-list`}
                    role="listbox"
                    className="absolute left-0 right-0 top-full mt-1 z-30 bg-[var(--bg-elevated)] border border-[var(--border-strong)] shadow-xl max-h-72 overflow-auto"
                >
                    {suggestions.map((s, i) => {
                        const active = i === highlight;
                        return (
                            <li
                                key={`${s.title}-${s.author}-${i}`}
                                role="option"
                                aria-selected={active}
                                data-testid={`${testId}-option-${i}`}
                                onMouseEnter={() => setHighlight(i)}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handlePick(s);
                                }}
                                className={`flex items-center gap-3 px-3 py-2 cursor-pointer border-b border-[var(--border)] last:border-b-0 transition-colors ${
                                    active ? "bg-[var(--bg-hover)]" : "hover:bg-[var(--bg-hover)]"
                                }`}
                            >
                                {s.cover ? (
                                    <img
                                        src={s.cover}
                                        alt=""
                                        className="w-7 h-10 object-cover border border-[var(--border-strong)] flex-shrink-0"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div className="w-7 h-10 bg-[var(--bg-strong)] border border-[var(--border-strong)] flex-shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm text-[var(--ink)] leading-tight truncate">
                                        {s.title}
                                    </p>
                                    {s.author && (
                                        <p className="text-xs text-[var(--ink-dim)] truncate">
                                            {s.author}
                                            {s.year ? ` · ${s.year}` : ""}
                                        </p>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
